import React, { useState } from 'react';
import { safeUrl } from '../../utils/safeUrl';

const JIRA_BASE_URL = process.env.REACT_APP_JIRA_BASE_URL || '';

/** Parse markdown links [text](url) and plain URLs into clickable elements. */
function linkify(text) {
  if (!text) return null;
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|https?:\/\/[^\s]+)/g);
  return parts.map((part, i) => {
    const mdMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (mdMatch) {
      return <a key={i} href={safeUrl(mdMatch[2])} target="_blank" rel="noopener noreferrer">{mdMatch[1]}</a>;
    }
    if (/^https?:\/\//.test(part)) {
      let label = 'link';
      if (part.includes('docs.google.com')) label = 'Transcript';
      else if (part.includes('meet.google.com') || part.includes('drive.google.com')) label = 'Recording';
      else if (part.includes('atlassian.net')) label = part.match(/PROJ-\d+/)?.[0] || 'Jira';
      return <a key={i} href={safeUrl(part)} target="_blank" rel="noopener noreferrer">{label}</a>;
    }
    return part;
  });
}

export default function SuggestionList({
  suggestions,
  onUpdate,
  onExecute,
  onBatchUpdate,
  onBatchExecute,
  showActions = true,
}) {
  const [editing, setEditing] = useState({});
  const [executing, setExecuting] = useState(new Set());

  if (!suggestions.length) {
    return <div className="mi-empty">No suggestions in this category.</div>;
  }

  const handleSaveEdit = async (id) => {
    const text = editing[id];
    if (text != null) {
      await onUpdate(id, 'modify', text);
      setEditing(prev => { const c = { ...prev }; delete c[id]; return c; });
    }
  };

  const handleExecute = async (id) => {
    setExecuting(prev => new Set([...prev, id]));
    await onExecute(id);
    setExecuting(prev => { const c = new Set(prev); c.delete(id); return c; });
  };

  const pendingIds = suggestions.filter(s => s.status === 'pending').map(s => s.id);

  return (
    <>
      {showActions && pendingIds.length > 1 && (
        <div className="mi-batch-actions">
          <button className="mi-btn mi-btn-success mi-btn-small" onClick={() => onBatchUpdate(pendingIds, 'accept')}>
            Accept All ({pendingIds.length})
          </button>
          <button className="mi-btn mi-btn-danger mi-btn-small" onClick={() => onBatchUpdate(pendingIds, 'reject')}>
            Reject All
          </button>
        </div>
      )}

      {suggestions.map(s => {
        const isEditing = editing[s.id] != null;
        const isExecuting = executing.has(s.id);
        const bodyText = s.type === 'jira_comment'
          ? (s.modified_text || s.comment_text)
          : (s.modified_text || s.suggested_summary);
        const issueKey = s.type === 'jira_comment' ? s.target_issue_key : null;
        const typeLabel = s.type === 'jira_comment' ? 'Comment' : 'New Task';

        return (
          <div key={s.id} className="mi-suggestion">
            <div className="mi-suggestion-header">
              <span className={`mi-suggestion-type ${s.type === 'jira_comment' ? 'comment' : 'task'}`}>
                {typeLabel}
              </span>
              <span className="mi-suggestion-person">{s.person_name}</span>
              <span className="mi-suggestion-sep">/</span>
              <span className="mi-suggestion-meeting">{s.event_summary}</span>
            </div>

            <div className="mi-suggestion-meta">
              {s.event_link
                ? <a href={safeUrl(s.event_link)} target="_blank" rel="noopener noreferrer">{s.event_summary} ({s.event_date_utc})</a>
                : <span>{s.event_summary} ({s.event_date_utc})</span>
              }
              {(s.transcript_link || s.recording_link) && (
                <span>
                  {s.transcript_link && <>{' '}<a href={safeUrl(s.transcript_link)} target="_blank" rel="noopener noreferrer">Transcript</a></>}
                  {s.recording_link && <>{' | '}<a href={safeUrl(s.recording_link)} target="_blank" rel="noopener noreferrer">Recording</a></>}
                </span>
              )}
            </div>

            {issueKey && (
              <div className="mi-suggestion-target">
                <a href={safeUrl(`${JIRA_BASE_URL}/${issueKey}`)} target="_blank" rel="noopener noreferrer">
                  {issueKey}: {s.target_issue_summary}
                </a>
              </div>
            )}

            {isEditing ? (
              <>
                <textarea
                  className="mi-suggestion-edit"
                  value={editing[s.id]}
                  onChange={e => setEditing(prev => ({ ...prev, [s.id]: e.target.value }))}
                />
                <div className="mi-suggestion-actions">
                  <button className="mi-btn mi-btn-success mi-btn-small" onClick={() => handleSaveEdit(s.id)}>Save</button>
                  <button className="mi-btn mi-btn-secondary mi-btn-small" onClick={() =>
                    setEditing(prev => { const c = { ...prev }; delete c[s.id]; return c; })
                  }>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="mi-suggestion-body">{linkify(bodyText)}</div>

                {s.transcript_quote && (
                  <div className="mi-suggestion-quote">{s.transcript_quote}</div>
                )}

                {s.execution_result && (
                  <div className="mi-suggestion-executed">
                    Created: {s.execution_result.issueKey || 'Done'}
                  </div>
                )}

                {showActions && !s.execution_result && (
                  <div className="mi-suggestion-actions">
                    {s.status === 'pending' && (
                      <>
                        <button className="mi-btn mi-btn-success mi-btn-small" onClick={() => onUpdate(s.id, 'accept')}>Accept</button>
                        <button className="mi-btn mi-btn-danger mi-btn-small" onClick={() => onUpdate(s.id, 'reject')}>Reject</button>
                        <button className="mi-btn mi-btn-secondary mi-btn-small" onClick={() => setEditing(prev => ({ ...prev, [s.id]: bodyText }))}>Edit</button>
                      </>
                    )}
                    {(s.status === 'accepted' || s.status === 'modified') && (
                      <button className="mi-btn mi-btn-primary mi-btn-small" disabled={isExecuting} onClick={() => handleExecute(s.id)}>
                        {isExecuting ? 'Creating...' : 'Create in Jira'}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </>
  );
}
